/**
 * Embedded Stripe SetupIntent modal — saves a card for autopay.
 *
 * Flow:
 *   1. On open: POST /accounts/:aid/autopay/setup-intent → { client_secret, setup_intent_id }
 *   2. Mount <Elements clientSecret> with <PaymentElement mode='setup' />
 *   3. On submit → stripe.confirmSetup({ redirect: 'if_required' })
 *   4. On success → POST /accounts/:aid/autopay/set-default { payment_method_id }
 *   5. Show success and call onSuccess() so the dashboard refreshes.
 */
import { useCallback, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { CheckCircle2, CreditCard, Loader2, X } from 'lucide-react';

import { paymentApi } from '@/lib/api';
import { AUTOPAY_MODAL } from '@/constants/testIds';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

function InnerAutopayForm({ accountId, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('collect'); // collect | saving | done

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError('');
    const { error: err, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });
    if (err) {
      setError(err.message || 'Unable to save card. Please try again.');
      setSubmitting(false);
      return;
    }
    const pmId = setupIntent?.payment_method;
    if (!pmId) {
      setError('Unable to save card. Please try again.');
      setSubmitting(false);
      return;
    }
    setPhase('saving');
    try {
      await paymentApi.setAutopayDefault(accountId, pmId);
      setPhase('done');
    } catch (apiErr) {
      setError(apiErr?.detail || 'Card was saved but autopay could not be enabled.');
    }
    setSubmitting(false);
  }, [stripe, elements, submitting, accountId]);

  if (phase === 'done') {
    return (
      <div
        className="flex flex-col items-center text-center px-2 py-6"
        data-testid={AUTOPAY_MODAL.successState}
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#DDF3E4] mb-4">
          <CheckCircle2 className="h-7 w-7 text-[#0F5132]" />
        </span>
        <h3 className="font-heading uppercase text-xl text-[#0B1930]">
          Autopay is on
        </h3>
        <p className="mt-2 text-sm text-[#4B5563] max-w-xs">
          We&apos;ll automatically charge this card on the due date of each new invoice.
        </p>
        <button
          type="button"
          onClick={() => {
            onSuccess?.();
            onClose();
          }}
          className="mt-6 inline-flex items-center justify-center rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors"
          data-testid={AUTOPAY_MODAL.successDoneBtn}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <p className="text-sm text-[#4B5563] leading-relaxed">
        Save a card and we&apos;ll automatically pay each new ZMEX invoice on its due date.
        You can turn autopay off anytime.
      </p>

      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <p
          className="text-sm text-red-600"
          data-testid={AUTOPAY_MODAL.errorMsg}
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A46B] hover:bg-[#B8925A] text-[#0B1930] px-4 py-3 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        data-testid={AUTOPAY_MODAL.submitBtn}
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {phase === 'saving' ? 'Enabling autopay…' : 'Saving card…'}
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4" />
            Save card &amp; enable autopay
          </>
        )}
      </button>

      <p className="text-[11px] text-center text-[#4B5563]">
        Stripe stores your card. ZMEX never sees the number.
      </p>
    </form>
  );
}

export default function AutopayModal({ open, onClose, accountId, onSuccess }) {
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setIntent(null);
    setError('');
    setLoading(true);
    (async () => {
      try {
        const { data } = await paymentApi.createSetupIntent(accountId);
        if (cancelled) return;
        setIntent(data);
      } catch (err) {
        if (cancelled) return;
        setError(err?.detail || 'Unable to start card setup.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, accountId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6"
      data-testid={AUTOPAY_MODAL.modal}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#EDE5D4] px-6 py-4 shrink-0">
          <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-lg">
            Set up Autopay
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#F5F1EA] transition-colors"
            aria-label="Close"
            data-testid={AUTOPAY_MODAL.closeBtn}
          >
            <X className="h-5 w-5 text-[#4B5563]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading && (
            <div className="flex items-center justify-center py-10 text-[#4B5563] gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Preparing secure form…</span>
            </div>
          )}
          {error && !loading && (
            <p className="text-sm text-red-600 text-center py-6">{error}</p>
          )}
          {intent && !loading && stripePromise && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: intent.client_secret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#C8A46B',
                    colorText: '#0B1930',
                    fontFamily: 'system-ui, sans-serif',
                    borderRadius: '10px',
                  },
                },
              }}
            >
              <InnerAutopayForm
                accountId={accountId}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            </Elements>
          )}
          {!stripePromise && !loading && (
            <p className="text-sm text-red-600 text-center py-6">
              Payments are not configured. Please contact ZMEX support.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
